'use client';

import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';

import { useCookieConsent } from '@packages/shared/src/hooks/useCookieConsent/useCookieConsent';
import { ConsentCategory } from '@packages/shared/src/utils/getCookieConsent/getCookieConsent';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';

import { ODOSCOPE_PARAMS_COOKIE_NAME, captureAttributionData } from '../utils/odoscopeAttribution';
import { useFeatureFlag } from '../utils/featureFlags/useFeatureFlag';
import { odoscopeAttribution } from '../activeFeatureFlags';

/**
 * Manages Odoscope attribution data for customer engagement and hyper-personalization.
 *
 * Captures marketing campaign parameters (UTM) and traffic source (referrer) on initial
 * external entry. Data is persisted for 30 days to enable Odoscope's real-time
 * personalization and look-alike-audience analysis.
 *
 * @odoscope Cookie format aligns with ODOSCOPE SDK patterns (osc-* namespace)
 * @gdpr Only active when marketing consent (C0004) is granted
 */
export const useOdoscopeAttribution = (): void => {
  const hasMarketingConsent = useCookieConsent(ConsentCategory.Marketing);
  const { setCookie, getCookies } = useCookies();
  const searchParams = useSearchParams();
  const { host } = useConfig();
  const isOdoscopeEnabled = useFeatureFlag(odoscopeAttribution);

  const captureTracking = useCallback(() => {
    // Feature flag check - disabled by default until backend schema is ready (SEARCH-4136)
    if (!isOdoscopeEnabled) return;

    const attributionData = captureAttributionData({
      searchParams,
      clientReferrer: document.referrer,
      domain: host.domain,
      existingCookieValue: getCookies()[ODOSCOPE_PARAMS_COOKIE_NAME],
    });

    if (!attributionData) return;

    const expiry = new Date();
    expiry.setTime(expiry.getTime() + 30 * 24 * 60 * 60 * 1000);

    setCookie(null, ODOSCOPE_PARAMS_COOKIE_NAME, JSON.stringify(attributionData), {
      expires: expiry,
      path: '/',
    });
  }, [searchParams, host.domain, getCookies, setCookie, isOdoscopeEnabled]);

  useEffect(() => {
    // Cookie expires naturally; no destroy to avoid false negatives from OneTrust domain mismatch
    if (hasMarketingConsent) {
      captureTracking();
    }
  }, [hasMarketingConsent, captureTracking]);
};
